import { CalendarClock, Gamepad2, MapPin, Repeat, Trophy, Users } from 'lucide-react'

import { MOCK_EVENTS, type MockEvent, type MockEventType } from '@/features/marketing/mockData/events'

/**
 * Events list mockup — surfaces the multi-type-in-one-feed pitch.
 * Four upcoming events, mix of types so visitors clock immediately
 * that tournaments sit next to gaming sessions, meetups, and streams
 * in the same calendar / feed.
 */
const TYPE_META: Record<MockEventType, { label: string; accent: string; bg: string; icon: typeof Trophy }> = {
  TOURNAMENT: {
    label: 'Tournament',
    accent: 'text-warning border-warning/40 bg-warning/10',
    bg: 'bg-warning/[0.04]',
    icon: Trophy,
  },
  GAMING_SESSION: {
    label: 'Session',
    accent: 'text-primary border-primary/40 bg-primary/10',
    bg: 'bg-primary/[0.04]',
    icon: Gamepad2,
  },
  MEETUP: {
    label: 'Meetup',
    accent: 'text-secondary border-secondary/40 bg-secondary/10',
    bg: 'bg-secondary/[0.04]',
    icon: MapPin,
  },
  STREAM: {
    label: 'Stream',
    accent: 'text-success border-success/40 bg-success/10',
    bg: 'bg-success/[0.04]',
    icon: CalendarClock,
  },
}

export function EventsListMockup({ rowCount = 4 }: { rowCount?: number }) {
  const rows = MOCK_EVENTS.slice(0, rowCount)
  return (
    <div className="divide-y divide-border/60">
      {rows.map((evt) => (
        <EventRow key={evt.id} event={evt} />
      ))}
    </div>
  )
}

function EventRow({ event }: { event: MockEvent }) {
  const meta = TYPE_META[event.type]
  const Icon = meta.icon
  return (
    <div className={`flex items-start gap-3 px-4 py-3 ${meta.bg}`}>
      <div className={`flex size-10 shrink-0 items-center justify-center rounded-md border ${meta.accent}`}>
        <Icon className="size-4" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`font-display inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[9px] font-bold tracking-[0.18em] uppercase ${meta.accent}`}>
            {meta.label}
          </span>
          {event.recurring && (
            <span className="font-display inline-flex items-center gap-1 rounded-md border border-border bg-bg/40 px-1.5 py-0.5 text-[9px] font-bold tracking-[0.15em] text-fg-subtle uppercase">
              <Repeat className="size-2.5" aria-hidden />
              Weekly
            </span>
          )}
          {event.isPaid && (
            <span className="font-display inline-flex items-center gap-1 rounded-md border border-warning/30 bg-warning/10 px-1.5 py-0.5 text-[9px] font-bold tracking-[0.15em] text-warning uppercase">
              £{((event.prizePoolPence ?? 0) / 100).toFixed(0)} pot
            </span>
          )}
        </div>
        <p className="mt-1 truncate text-sm font-semibold text-fg">{event.title}</p>
        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-fg-subtle">
          <span>{formatWhen(event.startsAt)}</span>
          {event.gameName && (
            <>
              <span aria-hidden>·</span>
              <span className="truncate">{event.gameName}</span>
            </>
          )}
          {event.venueName && (
            <>
              <span aria-hidden>·</span>
              <span className="truncate">{event.venueName}</span>
            </>
          )}
        </div>
      </div>
      {event.attendeeCap != null && (
        <span className="font-display ml-2 inline-flex items-center gap-1 self-center rounded-md border border-border bg-bg/40 px-2 py-0.5 text-[11px] font-bold text-fg-muted">
          <Users className="size-3" aria-hidden />
          {event.attendeeCount}/{event.attendeeCap}
        </span>
      )}
    </div>
  )
}

function formatWhen(iso: string): string {
  const target = Date.parse(iso)
  const diffMs = target - Date.parse('2026-06-16T20:00:00Z')
  if (diffMs <= 0) return 'now'
  const minutes = Math.round(diffMs / 60_000)
  if (minutes < 60) return `in ${minutes}m`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `in ${hours}h`
  const days = Math.round(hours / 24)
  return `in ${days}d`
}
